// The Get started card (components/list/GetStarted): the few things that make
// Matlist work for a household, each ticked off by what has actually happened,
// never by a click on the card. The two that matter most come first: a shared
// list is only useful once someone else is on it, and on iPhone a web app only
// feels like an app from the home screen.

export type StepId = 'invite' | 'install' | 'items' | 'recipe' | 'watch'

export interface Step {
  id: StepId
  done: boolean
}

export interface StepInput {
  /** People in the household, this one included. */
  members: number
  /** Running as an installed app (lib/standalone.ts). */
  installed: boolean
  /** The phone layout; installing is a phone matter, so desktop has no such step. */
  phone: boolean
  /** Anything on the list now or in its history. */
  hasItems: boolean
  recipes: number
  watchPaired: boolean
  /** The Garmin step still applies (prefs.watchStep; off after "I don't have a Garmin"). */
  watchStep: boolean
}

export function getStartedSteps(i: StepInput): Step[] {
  const steps: Step[] = [{ id: 'invite', done: i.members > 1 }]
  if (i.phone) steps.push({ id: 'install', done: i.installed })
  steps.push(
    { id: 'items', done: i.hasItems },
    { id: 'recipe', done: i.recipes > 0 },
    { id: 'watch', done: i.watchPaired || !i.watchStep },
  )
  return steps
}

/** The card shows until every step is done, unless it was hidden. */
export function showGetStarted(steps: Step[], enabled: boolean): boolean {
  return enabled && steps.some(s => !s.done)
}

export type InstallHelp = 'ios' | 'android-prompt' | 'android' | 'other'

/** How this browser adds a web app to the home screen. */
export function installHelp(userAgent: string, canPrompt: boolean): InstallHelp {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios'
  if (/Android/i.test(userAgent)) return canPrompt ? 'android-prompt' : 'android'
  return 'other'
}

/** The text an invite shares: where to go and the code to type (joining is by code only). */
export function inviteText(householdName: string, code: string, url: string): string {
  return `Join our grocery list “${householdName}” on Matlist: open ${url}, create an account, choose Join household and enter ${code}.`
}
